import { RuleId } from '@wix/a11y-rules';
import { useExperiments } from '@wix/fe-essentials/react';
import { produce } from 'immer';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo, useRef, useState } from 'react';
import { ruleApplyTriggeredAtom } from '../../../../../../../atoms/session-atoms';
import { BiModalTypes } from '../../../../../../../bi/types';
import { usePanelStateAtom } from '../../../../../../../hooks/rules-state/use-panel-state-atom';
import { PanoramaErrorNames } from '../../../../../../../panorama/constants';
import { useGlobalSectionState } from '../../../../../../../rule-mappers/shared/use-global-section-state';
import { useRuleBase } from '../../../../../../../rule-mappers/shared/use-rule-base';
import { showSaveBeforeCloseModal } from '../../../../../../modals/show-save-before-leave-modal';
import { showSaveErrorModal } from '../../../../../../modals/show-save-error-modal';
import { showStopGeneratingSuggestionsModal } from '../../../../../../modals/show-stop-generating-suggestions-modal';
import { convertScanStatusToUiStatus } from '../../../../utils';
import { buildGlobalSectionInputMap, calculateStatusAfterApply, patchScanResultsAfterApply, prepareChangedAltData, splitByPageType } from './prepare-alt-data';
export const useApplyChanges = _ref => {
  var _ruleState$state2;
  let {
    images,
    modifiedImageIds,
    setModifiedImageIds,
    hasChanges,
    setHasChanges,
    originalPropsMap,
    altRulesProps,
    pageId,
    missingCount,
    navigateToPanel,
    resetToOriginal,
    commitCurrentAsOriginal,
    setShowSummary,
    isGenerating,
    setIsGenerating,
    unreviewedAiImageIds,
    altHandlers,
    staticAltHandlers,
    a11yWizardAdapter,
    editorHost,
    biLogger,
    reportErrorPanorama,
    t
  } = _ref;
  const [isSaving, setIsSaving] = useState(false);
  const [showPopoverAiWarning, setShowPopoverAiWarning] = useState(false);
  const {
    experiments
  } = useExperiments();
  const tools = useMemo(() => ({
    isExperimentEnabled: spec => experiments.enabled(spec)
  }), [experiments]);
  const {
    getGroupData,
    getRuleState,
    panelState,
    getPagesData,
    setGroupExecutionResults
  } = usePanelStateAtom();
  const pagesData = getPagesData();
  const ruleState = getRuleState(pageId, RuleId.STATIC_ALT_TEXT);
  const setRuleApplyTriggered = useSetAtom(ruleApplyTriggeredAtom);
  const {
    updateRuleStateAndVerifyIfNeeded,
    setIsApplyTriggered
  } = useRuleBase();
  const {
    updateGlobalSectionStateIfNeeded,
    getGlobalSectionModifiedComponentIds
  } = useGlobalSectionState();

  // Refs for unsaved changes handlers
  const resetToOriginalRef = useRef(resetToOriginal);
  resetToOriginalRef.current = resetToOriginal;

  // Apply changes handler
  const handleApply = useCallback(async () => {
    var _ruleState$state;
    if (modifiedImageIds.size === 0) {
      return;
    }

    // Check for unreviewed AI-generated images before applying
    if (!showPopoverAiWarning && unreviewedAiImageIds.current.size > 0) {
      setShowPopoverAiWarning(true);
      biLogger.modalView({
        modalType: BiModalTypes.ReviewRecentChanges
      });
      return;
    } else if (showPopoverAiWarning) {
      setShowPopoverAiWarning(false);
    }
    setIsSaving(true);
    const stateMap = (ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.savedImages) ?? new Map();

    // Prepare data using utility functions
    const changedAltData = prepareChangedAltData(images, modifiedImageIds, originalPropsMap);

    // BI logging for apply click
    biLogger.taskElementApplyClick({
      elementIndexList: changedAltData.map(rule => altRulesProps.findIndex(ruleProp => ruleProp.id === rule.id)),
      elementStatusList: changedAltData.map(altData => altData.status),
      elementValueList: changedAltData.map(rule => rule.alt)
    });
    const {
      staticChangedAltData,
      verticalChangedAltData
    } = splitByPageType(changedAltData, pagesData);

    // Update state map
    const updatedStateMap = produce(stateMap, draft => {
      changedAltData.forEach(altData => {
        draft.set(altData.id, altData);
      });
    });

    // Update global section state if needed
    const updatedGlobalSectionDataMap = staticChangedAltData.length > 0 ? updateGlobalSectionStateIfNeeded(buildGlobalSectionInputMap(changedAltData)) : panelState.globalSectionComponentsDataMap;
    const statusAfterApply = calculateStatusAfterApply(images, tools);
    try {
      await Promise.all([altHandlers.setBulkAltText(verticalChangedAltData), staticAltHandlers == null || staticAltHandlers.setBulkAltText == null ? void 0 : staticAltHandlers.setBulkAltText(staticChangedAltData)]);
      a11yWizardAdapter.triggerEditorSave().catch(error => {
        reportErrorPanorama(error, PanoramaErrorNames.updateAltText);
      });
      patchScanResultsAfterApply({
        changedAltData,
        getGroupData,
        pageId,
        setGroupExecutionResults,
        tools
      });
      updateRuleStateAndVerifyIfNeeded(pageId, RuleId.STATIC_ALT_TEXT, {
        savedImages: updatedStateMap,
        status: convertScanStatusToUiStatus(statusAfterApply)
      }, getGlobalSectionModifiedComponentIds(updatedGlobalSectionDataMap));

      // Clear modified tracking
      setModifiedImageIds(new Set());
      setHasChanges(false);

      // Commit current state as the new baseline so discard reverts to post-save state
      commitCurrentAsOriginal();

      // Reset summary view (matches original production behavior)
      setShowSummary(false);

      // Navigate back if all issues resolved
      if (missingCount === 0) {
        navigateToPanel();
      }
    } catch (error) {
      console.error('Failed to save alt text changes:', error);
      reportErrorPanorama(error, PanoramaErrorNames.updateAltText);
      await showSaveErrorModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        errorMessage: error == null ? void 0 : error.message,
        handleTryAgain: () => handleApply(),
        t
      });
    } finally {
      setIsSaving(false);
      setIsApplyTriggered(true);
      setRuleApplyTriggered({
        pageId,
        ruleId: RuleId.STATIC_ALT_TEXT
      });
    }
  }, [a11yWizardAdapter, altHandlers, altRulesProps, biLogger, commitCurrentAsOriginal, editorHost, getGlobalSectionModifiedComponentIds, getGroupData, images, missingCount, modifiedImageIds, navigateToPanel, originalPropsMap, pageId, pagesData, panelState.globalSectionComponentsDataMap, reportErrorPanorama, ruleState == null || (_ruleState$state2 = ruleState.state) == null ? void 0 : _ruleState$state2.savedImages, setGroupExecutionResults, setHasChanges, setIsApplyTriggered, setModifiedImageIds, setRuleApplyTriggered, setShowSummary, showPopoverAiWarning, staticAltHandlers, t, tools, unreviewedAiImageIds, updateGlobalSectionStateIfNeeded, updateRuleStateAndVerifyIfNeeded]);

  // Ref for handleApply for unsaved changes
  const handleApplyRef = useRef(handleApply);
  handleApplyRef.current = handleApply;

  // Show before close modal
  const showBeforeCloseModal = useCallback((afterApplyFunc, isGeneratingAi) => {
    const shouldShowSaveBeforeCloseModal = hasChanges || modifiedImageIds.size > 0;
    if (isGeneratingAi) {
      showStopGeneratingSuggestionsModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        handleApply: () => {},
        handleDiscard: () => {
          setIsGenerating(false);
          showBeforeCloseModal(afterApplyFunc, false);
        },
        t
      });
    } else if (shouldShowSaveBeforeCloseModal) {
      showSaveBeforeCloseModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        handleApply: async () => {
          await handleApplyRef.current();
          afterApplyFunc();
        },
        handleDiscard: afterApplyFunc,
        t
      });
    }
  }, [a11yWizardAdapter, biLogger, editorHost, hasChanges, modifiedImageIds.size, setIsGenerating, t]);

  // Cancel handler — stop-generating modal is handled by rules-panel-ui guard,
  // so this only needs to stop generation and handle unsaved changes.
  const handleCancel = useCallback(() => {
    if (isGenerating) {
      setIsGenerating(false);
    }
    const hasUnsavedChanges = hasChanges || modifiedImageIds.size > 0;
    if (hasUnsavedChanges) {
      showBeforeCloseModal(navigateToPanel, false);
    } else {
      navigateToPanel();
    }
  }, [hasChanges, isGenerating, modifiedImageIds.size, navigateToPanel, setIsGenerating, showBeforeCloseModal]);

  // Unsaved changes handlers for context
  const unsavedChangesHandlers = useMemo(() => ({
    setUnsavedChanges: () => {},
    showSaveBeforeLeaveModal: (apply, discard) => showSaveBeforeCloseModal({
      a11yWizardAdapter,
      biLogger,
      editorHost,
      handleApply: apply,
      handleDiscard: discard,
      t
    }),
    unsavedChanges: {
      apply: () => handleApplyRef.current(),
      discard: () => resetToOriginalRef.current(),
      exists: hasChanges || modifiedImageIds.size > 0
    }
  }), [a11yWizardAdapter, biLogger, editorHost, hasChanges, modifiedImageIds.size, t]);
  return {
    handleApply,
    handleCancel,
    isSaving,
    setIsSaving,
    setShowPopoverAiWarning,
    showBeforeCloseModal,
    showPopoverAiWarning,
    unsavedChangesHandlers
  };
};
//# sourceMappingURL=use-apply-changes.js.map